import { useState } from 'react';
import { Loader2, Package, Ruler } from 'lucide-react';
import {
  createClientCrmOrder,
  ensureClientDeal,
  ensureCrmOrderMeasurement,
} from '../../api';
import { formatAdminPanelError } from '@/lib/adminApiError';
import { openOrderComposeCatalog } from '../../orderComposeStore';
import { useNavigate } from 'react-router-dom';
import { AdminMeasurementIntakeFields } from './AdminMeasurementIntakeFields';

type Props = {
  userId: number;
  primaryDealId?: number | null;
  hapticSelection: () => void;
  hapticSuccess: () => void;
  onOrderCreated: (orderId: number, orderNumber: string) => void;
  onMeasurementCreated?: (measurementId: number, orderId: number) => void;
  onDealsChanged?: () => void;
};

function extract409(err: unknown): { orderId?: number; orderNumber?: string; message?: string } | null {
  const ax = err as {
    response?: { status?: number; data?: { detail?: string | { message?: string; existing_order_id?: number; order_number?: string } } };
  };
  if (ax.response?.status !== 409) return null;
  const d = ax.response.data?.detail;
  if (typeof d === 'object' && d) {
    return {
      orderId: d.existing_order_id,
      orderNumber: d.order_number,
      message: d.message,
    };
  }
  return { message: typeof d === 'string' ? d : 'Сделка уже открыта' };
}

export function AdminClientQuickActions({
  userId,
  primaryDealId,
  hapticSelection,
  hapticSuccess,
  onOrderCreated,
  onMeasurementCreated,
  onDealsChanged,
}: Props) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState<'deal' | 'catalog' | 'measurement' | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showMeasForm, setShowMeasForm] = useState(false);
  const [city, setCity] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [measNotes, setMeasNotes] = useState('');
  const [activeOrderId, setActiveOrderId] = useState<number | null>(primaryDealId ?? null);

  const openDeal = async (opts?: { openCatalog?: boolean }) => {
    hapticSelection();
    setBusy(opts?.openCatalog ? 'catalog' : 'deal');
    setError(null);
    try {
      const res = await ensureClientDeal(userId);
      setActiveOrderId(res.order_id);
      hapticSuccess();
      onDealsChanged?.();
      onOrderCreated(res.order_id, res.order_number);
      if (opts?.openCatalog) {
        openOrderComposeCatalog(navigate, { orderId: res.order_id, orderNumber: res.order_number });
      }
    } catch (err: unknown) {
      const dup = extract409(err);
      if (dup?.orderId) {
        setActiveOrderId(dup.orderId);
        onOrderCreated(dup.orderId, dup.orderNumber || `ONV`);
        if (opts?.openCatalog && dup.orderId) {
          openOrderComposeCatalog(navigate, {
            orderId: dup.orderId,
            orderNumber: dup.orderNumber || '',
          });
        }
      } else {
        setError(formatAdminPanelError(err, 'Не удалось открыть сделку'));
      }
    } finally {
      setBusy(null);
    }
  };

  const createNewDeal = async () => {
    hapticSelection();
    setBusy('new');
    setError(null);
    try {
      const res = await createClientCrmOrder(userId, undefined, { force_new: true });
      setActiveOrderId(res.order_id);
      hapticSuccess();
      onDealsChanged?.();
      onOrderCreated(res.order_id, res.order_number);
    } catch (err: unknown) {
      const dup = extract409(err);
      if (dup?.orderId) {
        const ok = window.confirm(
          `${dup.message || 'У клиента уже есть сделка'} ${dup.orderNumber || ''}. Открыть её?`,
        );
        if (ok && dup.orderId) {
          onOrderCreated(dup.orderId, dup.orderNumber || '');
          return;
        }
      }
      setError(formatAdminPanelError(err, 'Не удалось создать сделку'));
    } finally {
      setBusy(null);
    }
  };

  const submitMeasurement = async () => {
    if (!preferredTime.trim()) {
      setError('Выберите день и интервал выезда.');
      return;
    }
    hapticSelection();
    setBusy('measurement');
    setError(null);
    try {
      let orderId = activeOrderId ?? primaryDealId ?? null;
      if (!orderId) {
        const deal = await ensureClientDeal(userId);
        orderId = deal.order_id;
        setActiveOrderId(orderId);
      }
      const res = await ensureCrmOrderMeasurement(orderId, {
        city: city.trim() || undefined,
        preferred_time: preferredTime.trim(),
        notes: measNotes.trim() || undefined,
      });
      hapticSuccess();
      setShowMeasForm(false);
      setCity('');
      setMeasNotes('');
      onDealsChanged?.();
      onOrderCreated(orderId, res.board_card?.order?.order_number || '');
      onMeasurementCreated?.(res.measurement_id, orderId);
    } catch (err: unknown) {
      setError(formatAdminPanelError(err, 'Не удалось запланировать замер'));
    } finally {
      setBusy(null);
    }
  };

  const orderIdForCatalog = activeOrderId ?? primaryDealId;

  return (
    <div className="space-y-2">
      <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--cyan)' }}>
        Быстрые действия
      </p>
      <button
        type="button"
        disabled={Boolean(busy)}
        onClick={() => void openDeal()}
        className="press w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-bold min-h-[44px] disabled:opacity-50"
        style={{ background: 'rgba(99,179,237,0.22)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.4)' }}
      >
        {busy === 'deal' ? <Loader2 size={14} className="animate-spin" /> : <Package size={14} />}
        Открыть сделку
      </button>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled={Boolean(busy) || !orderIdForCatalog}
          onClick={() => {
            if (!orderIdForCatalog) return;
            hapticSelection();
            openOrderComposeCatalog(navigate, { orderId: orderIdForCatalog, orderNumber: '' });
          }}
          className="press flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[11px] font-bold min-h-[44px] disabled:opacity-40"
          style={{ background: 'rgba(72,187,120,0.12)', color: '#68D391', border: '1px solid rgba(72,187,120,0.3)' }}
        >
          {busy === 'catalog' ? <Loader2 size={14} className="animate-spin" /> : <Package size={14} />}
          Состав + каталог
        </button>
        <button
          type="button"
          disabled={Boolean(busy)}
          onClick={() => {
            hapticSelection();
            setShowMeasForm((v) => !v);
            setError(null);
          }}
          className="press flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[11px] font-bold min-h-[44px] disabled:opacity-50"
          style={{ background: 'rgba(159,122,234,0.12)', color: '#B794F4', border: '1px solid rgba(159,122,234,0.35)' }}
        >
          {busy === 'measurement' ? <Loader2 size={14} className="animate-spin" /> : <Ruler size={14} />}
          Замер
        </button>
      </div>
      <button
        type="button"
        disabled={Boolean(busy)}
        onClick={() => void createNewDeal()}
        className="press w-full py-2 rounded-xl text-[10px] font-semibold disabled:opacity-50"
        style={{ color: 'var(--text-3)', border: '1px solid var(--border-subtle)' }}
      >
        {busy === 'new' ? '…' : 'Новая сделка (другой объект)'}
      </button>

      {showMeasForm && (
        <div
          className="rounded-xl p-3 space-y-2"
          style={{ background: 'rgba(159,122,234,0.08)', border: '1px solid rgba(159,122,234,0.28)' }}
        >
          <p className="text-[10px] leading-snug" style={{ color: 'var(--text-3)' }}>
            Замер привязывается к открытой сделке ONV — отдельная заявка не создаётся.
          </p>
          <AdminMeasurementIntakeFields
            city={city}
            preferredTime={preferredTime}
            notes={measNotes}
            onCity={setCity}
            onPreferredTime={setPreferredTime}
            onNotes={setMeasNotes}
            disabled={busy === 'measurement'}
            onHaptic={hapticSelection}
          />
          <button
            type="button"
            disabled={busy === 'measurement'}
            onClick={() => void submitMeasurement()}
            className="press w-full py-2.5 rounded-xl text-xs font-bold min-h-[44px] disabled:opacity-50"
            style={{ background: 'rgba(159,122,234,0.25)', color: '#E9D8FD' }}
          >
            Запланировать замер
          </button>
        </div>
      )}

      {error && (
        <p className="text-[10px] px-2 py-1.5 rounded-lg" style={{ background: 'rgba(252,129,74,0.12)', color: '#FC814A' }}>
          {error}
        </p>
      )}
    </div>
  );
}
